import React, { useState, useEffect, useRef } from 'react';
import SearchBar from '../components/SearchBar';
import CategoryFilter from '../components/CategoryFilter';
import PropertyCard from '../components/PropertyCard';
import SEO from '../components/SEO';
import { propertiesAPI } from '../services/api';
import { toast } from 'sonner';

const HomePage = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchFilters, setSearchFilters] = useState({
    location: '',
    guests: null,
    minPrice: null,
    maxPrice: null,
    bedrooms: null
  });
  const propertiesSectionRef = useRef(null);

  useEffect(() => {
    fetchProperties();
  }, [selectedCategory, searchFilters]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      
      if (selectedCategory) {
        params.category = selectedCategory;
      }
      
      // Add search filters
      if (searchFilters.location) {
        params.location = searchFilters.location;
      }
      if (searchFilters.guests) {
        params.guests = searchFilters.guests;
      }
      if (searchFilters.minPrice) {
        params.minPrice = searchFilters.minPrice;
      }
      if (searchFilters.maxPrice && searchFilters.maxPrice < 50000) {
        params.maxPrice = searchFilters.maxPrice;
      }
      
      const response = await propertiesAPI.getAll(params);
      // Show all admin-approved properties. If the host's PAN is not yet verified,
      // the property card displays a "KYC Pending" badge and the Reserve button
      // is disabled — but the listing remains visible for discovery.
      setProperties(response.data || []);
    } catch (err) {
      setError('Failed to load properties. Please try again later.');
      toast.error('Failed to load properties');
      console.error('Error fetching properties:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (filters) => {
    setSearchFilters(prev => ({
      ...prev,
      location: filters.location || '',
      guests: filters.guests || null
    }));
    
    // Clear category filter when searching by location
    if (filters.location) {
      setSelectedCategory(null);
    }
    
    if (filters.location) {
      toast.success(`Searching properties in "${filters.location}"`);
    }
  };

  const handleFiltersChange = (filters) => {
    setSearchFilters(prev => ({
      ...prev,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      guests: filters.guests || prev.guests,
      bedrooms: filters.bedrooms
    }));
    
    const hasActiveFilters = filters.minPrice > 0 || filters.maxPrice < 50000 || filters.guests || filters.bedrooms;
    if (hasActiveFilters) {
      toast.success('Filters applied');
    }
  };

  const clearSearch = () => {
    setSearchFilters({ 
      location: '', 
      guests: null, 
      minPrice: null, 
      maxPrice: null, 
      bedrooms: null 
    });
  };

  const handleInspirationClick = (category) => {
    // Clear other filters and set the category
    setSearchFilters({ 
      location: '', 
      guests: null, 
      minPrice: null, 
      maxPrice: null, 
      bedrooms: null 
    });
    setSelectedCategory(category);
    
    // Scroll to properties section
    if (propertiesSectionRef.current) {
      propertiesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    toast.success(`Showing ${category} properties`);
  };

  const hasActiveFilters = searchFilters.location || 
    searchFilters.minPrice > 0 || 
    (searchFilters.maxPrice && searchFilters.maxPrice < 50000) ||
    searchFilters.guests > 1 ||
    searchFilters.bedrooms;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        path="/"
        title="Sustainable Homestays, Farmstays & B&Bs in India"
        description="Book unique homestays, farmstays, villas and B&Bs across India. Verified hosts, instant confirmation, sustainable stays — discover your perfect holiday home on GeoStays."
        keywords="homestays in India, farmstays in India, villas in India, B&B India, holiday homes, weekend getaways India, sustainable homestays, boutique villas, private pool villas India, beach homestays, mountain homestays"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'GeoStays',
          url: 'https://geostays.co',
          logo: 'https://customer-assets.emergentagent.com/job_stayswap-3/artifacts/greklbdc_GEOSTAYS-01.jpg',
          description: 'Curated marketplace for sustainable homestays, farmstays and B&Bs across India',
          sameAs: [],
        }}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Find Your Perfect Holiday Home
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover unique homes and experiences across India
            </p>
          </div>
          <SearchBar variant="hero" onSearch={handleSearch} />
        </div>
      </section>

      {/* Category Filters */}
      <CategoryFilter
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        onFiltersChange={handleFiltersChange}
      />

      {/* Properties Grid */}
      <section ref={propertiesSectionRef} className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Active Search/Filter Indicator */}
        {hasActiveFilters && (
          <div className="mb-6 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-green-800">
                Active filters:
                {searchFilters.location && <strong className="ml-1">"{searchFilters.location}"</strong>}
                {searchFilters.minPrice > 0 && <span className="ml-2">• Min ₹{searchFilters.minPrice}</span>}
                {searchFilters.maxPrice && searchFilters.maxPrice < 50000 && <span className="ml-2">• Max ₹{searchFilters.maxPrice}</span>}
                {searchFilters.guests > 1 && <span className="ml-2">• {searchFilters.guests}+ guests</span>}
                {searchFilters.bedrooms && <span className="ml-2">• {searchFilters.bedrooms}+ bedrooms</span>}
              </span>
            </div>
            <button
              onClick={clearSearch}
              className="text-green-700 hover:text-green-900 font-medium text-sm"
            >
              Clear filters
            </button>
          </div>
        )}
        
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7CB342]"></div>
            <p className="mt-4 text-gray-600">Loading properties...</p>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-500 text-lg">{error}</p>
            <button
              onClick={fetchProperties}
              className="mt-4 px-6 py-2 bg-[#7CB342] text-white rounded-lg hover:bg-[#6da036] transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {properties.map((property) => (
                <PropertyCard key={property._id || property.id} property={property} />
              ))}
            </div>

            {properties.length === 0 && (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg mb-4">
                  {hasActiveFilters 
                    ? 'No properties found matching your filters' 
                    : 'No properties found in this category'}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearSearch}
                    className="px-6 py-2 bg-[#7CB342] text-white rounded-lg hover:bg-[#6da036] transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </section>

      {/* Continue Exploring */}
      <section className="bg-gray-50 py-16 mt-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Inspiration for your next trip</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div 
              className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer transform hover:-translate-y-1"
              onClick={() => handleInspirationClick('Mountains')}
              data-testid="inspiration-mountains"
            >
              <img
                src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600"
                alt="Mountain escapes"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="font-semibold text-lg mb-2">Mountain Escapes</h3>
                <p className="text-gray-600 text-sm">Find peace in the hills</p>
              </div>
            </div>
            <div 
              className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer transform hover:-translate-y-1"
              onClick={() => handleInspirationClick('Beachfront')}
              data-testid="inspiration-beachfront"
            >
              <img
                src="https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=600"
                alt="Beach homes"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="font-semibold text-lg mb-2">Beach Homes</h3>
                <p className="text-gray-600 text-sm">Relax by the ocean</p>
              </div>
            </div>
            <div 
              className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer transform hover:-translate-y-1"
              onClick={() => handleInspirationClick('Rural Homestay')}
              data-testid="inspiration-countryside"
            >
              <img
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600"
                alt="Rural Homestay"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="font-semibold text-lg mb-2">Rural Homestays</h3>
                <p className="text-gray-600 text-sm">Experience rural charm</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;